/**
 * ContinueWithGoogleButton — entry point for the Cognito Hosted-UI Google OAuth
 * flow, rendered on the shared login/register page (LoginPage.tsx, RegisterPage.tsx).
 *
 * Implements google-sign-in/US-001 (T5, AC-001.1, AC-001.2). Reuses the shadcn/ui
 * Button + LoginPage's Card/CardFooter design tokens and the Loader2 spinner
 * pattern already used on LoginPage.tsx (T5 reuse_details).
 */

import { useState } from 'react';
import { signInWithRedirect, signOut } from 'aws-amplify/auth';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { isOAuthConfigured } from '@/lib/amplifyConfig';
import {
  captureReferralForOAuth,
  captureOAuthIntent,
  captureConsentForOAuth,
  type OAuthIntent,
} from '@/services/referralCapture';

interface ContinueWithGoogleButtonProps {
  /**
   * Optional typed referral code (e.g. RegisterPage's referralCode state).
   * Implements google-sign-in/US-005 (T4) / AC-005.1. Captured into
   * sessionStorage immediately before the OAuth redirect fires; when
   * omitted, captureReferralForOAuth falls back to the `?ref=` URL param
   * (LoginPage has no referral field, so it never passes this prop).
   */
  referralCode?: string;
  /**
   * BUG-20260713-020205 (D2, Option B) / T-6: which entry point initiated
   * this redirect — 'login' (LoginPage's Google button, sign-in-only) or
   * 'signup' (every RegisterPage Google button usage, including the
   * EMAIL_EXISTS reverse-flow CTA). Defaults to 'signup' so a caller that
   * forgets to pass this prop FAILS SAFE toward allowing registration
   * (signup never bounces on the OAuth callback) rather than accidentally
   * locking out a legitimate new signup. Captured into sessionStorage
   * immediately before the redirect fires, alongside the referral code.
   */
  intent?: OAuthIntent;
  /**
   * consent-on-google-signup: when true (RegisterPage's signup door), the
   * button is disabled until `consentAccepted` is true and captures that
   * acceptance into sessionStorage before the redirect, so OAuthCallbackPage
   * writes the consent record right after provisioning — collapsing the
   * post-login consent modal into the register-page T&C checkbox for the
   * Google signup door. LoginPage (sign-in only) omits it, so existing users
   * are never asked to re-consent at the button. Defaults to false (off).
   */
  requireConsent?: boolean;
  /**
   * The RegisterPage T&C/Privacy checkbox value (tncChecked). Only meaningful
   * when `requireConsent` is true: it gates the button (disabled until true)
   * and is captured on click for the post-callback consent write. Defaults to
   * false.
   */
  consentAccepted?: boolean;
  /**
   * promo-code US-005 (T2) / FR-2.7 / AC-005.1 / AC-005.5: true when
   * RegisterPage's signup gate has settled the entered promo/referral code as
   * invalid for the CURRENT field value. Composes with `consentBlocked` (this
   * button disables when EITHER is true) so the Google door is gated from the
   * SAME RegisterPage state that drives the native Sign Up button. Only the
   * RegisterPage signup door passes this prop — LoginPage never does, so
   * sign-in is never gated by a code. Defaults to false.
   */
  codeBlocked?: boolean;
  /**
   * promo-code US-005 (T2) / AC-005.5: the dash-free inline copy to render
   * when `codeBlocked` is the (sole) reason the button is disabled. RegisterPage
   * owns the wording so both doors show identical copy. Falls back to a
   * dash-free default when omitted.
   */
  codeBlockedNotice?: string;
  /**
   * Always-clickable buttons (2026-08-04): opts this button OUT of disabling itself on the
   * consent and code gates, moving both to CLICK TIME. Return false to abort the redirect
   * (the caller has shown its own explanation); return true to proceed.
   *
   * PASSING THIS CHANGES TWO THINGS, and they go together:
   *   1. `consentBlocked` / `codeGateBlocked` no longer disable the button. Only an
   *      in-flight redirect still does, so a double tap cannot fire two OAuth redirects.
   *   2. The two inline "why is this disabled" notices below are suppressed, because the
   *      button is not disabled and the caller's pop-up now carries that message. Leaving
   *      them would put a standing error under a live button.
   *
   * The consent CAPTURE is untouched: when the validator passes, `consentAccepted` is still
   * written to sessionStorage exactly as before, so a Google signup cannot reach the callback
   * without a real affirmative tick.
   *
   * Optional, and absent everywhere except RegisterPage. LoginPage passes nothing and keeps
   * its existing disable-driven behaviour unchanged.
   */
  onValidate?: () => boolean;
}

const DEFAULT_CODE_BLOCKED_NOTICE =
  'That code is not valid. Please check it, or clear the field to continue with Google.';

export function ContinueWithGoogleButton({
  referralCode,
  intent = 'signup',
  requireConsent = false,
  consentAccepted = false,
  codeBlocked = false,
  codeBlockedNotice,
  onValidate,
}: ContinueWithGoogleButtonProps = {}) {
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // T5 validation_rules: rendered null (not a disabled stub) when OAuth is not
  // configured — password/email login must never be blocked by a mis-provisioned
  // Google OAuth environment (T1 error_handling).
  if (!isOAuthConfigured) {
    return null;
  }

  // consent-on-google-signup: when this is the signup door, the button must not
  // proceed until the parent has ticked the T&C/Privacy box — mirrors the
  // RegisterPage Sign-Up button's tncChecked gate exactly.
  const consentBlocked = requireConsent && !consentAccepted;
  // promo-code US-005 (T2) / AC-005.1 / FR-2.7: compose with the consent gate.
  // `codeBlocked` defaults false so LoginPage and any caller that omits it is
  // never gated by a code (AC-005.2 no-regression).
  const codeGateBlocked = codeBlocked === true;
  // Always-clickable buttons (2026-08-04): with a click-time validator supplied, neither gate
  // disables the button any more — `onValidate` enforces them on click instead. Without one,
  // this is the shipped disable-driven behaviour, unchanged (LoginPage's path).
  const gatesDisableButton = onValidate === undefined;
  const blocked = gatesDisableButton && (consentBlocked || codeGateBlocked);

  const handleGoogleClick = async () => {
    if (redirecting) return; // ignore double-click while a redirect is already in flight
    // promo-code US-005 (T2): defensive guard mirrors the shipped consentBlocked
    // guard — a programmatic click cannot bypass either disable reason. Defense
    // in depth only: the account is still never server-blocked (edge case 1).
    if (blocked) return;
    // Always-clickable buttons (2026-08-04): the same gates, evaluated now instead of at
    // render. A false return means the caller has shown the parent what is missing, so this
    // click must not capture consent or redirect.
    if (onValidate && !onValidate()) return;
    setError(null);
    setRedirecting(true);
    // google-sign-in/US-005 (T4) / AC-005.1: capture the referral code into
    // sessionStorage BEFORE the redirect fires — this is the ONLY transport
    // that survives the full-page Cognito Hosted-UI navigation (Amplify owns
    // OAuth 'state', so it cannot carry the code). Never throws (T3 contract),
    // so this call cannot break the redirect itself.
    captureReferralForOAuth(referralCode);
    // BUG-20260713-020205 (D2, Option B) / T-6: capture which entry point
    // (login vs signup) initiated this redirect, using the SAME sessionStorage
    // transport pattern (never throws). OAuthCallbackPage reads-and-clears
    // this to enforce login-page-Google-is-sign-in-only post-callback.
    captureOAuthIntent(intent);
    // consent-on-google-signup: persist the parent's affirmative T&C/Privacy
    // acceptance (the RegisterPage checkbox) across the Hosted-UI redirect using
    // the SAME sessionStorage transport, so OAuthCallbackPage writes the consent
    // record via POST /auth/consent right after provisioning. Only the signup
    // door sets requireConsent; guarded/never-throws (referral-capture contract).
    if (requireConsent) {
      captureConsentForOAuth(consentAccepted === true);
    }
    try {
      await signInWithRedirect({ provider: 'Google' });
    } catch (e) {
      // BUG-20260713-152000: Amplify v6 throws UserAlreadyAuthenticatedException
      // when a prior session still lingers in Amplify's token store — e.g. a user
      // who is already signed in, or a login-page "new user" who was bounced to
      // /register (D2, Option B) while their federated session was still active.
      // In that state signInWithRedirect refuses to start, surfacing the generic
      // "unavailable" message even though OAuth is perfectly healthy. Recover by
      // clearing the lingering LOCAL session (signOut() with no {global:true} does
      // a local token clear WITHOUT the Hosted-UI /logout browser redirect that
      // useAuth.logout performs — see useAuth.ts:196) and retrying the redirect
      // once. The Cognito Hosted-UI cookie is intentionally left intact so the
      // retry silently re-authenticates the same Google account (no re-pick).
      if (e instanceof Error && e.name === 'UserAlreadyAuthenticatedException') {
        try {
          await signOut();
          await signInWithRedirect({ provider: 'Google' });
          return;
        } catch (retryErr) {
          console.error(
            '[ContinueWithGoogleButton] retry after clearing stale session failed',
            retryErr
          );
        }
      }
      // Synchronous misconfiguration failure — never crash the login page.
      setRedirecting(false);
      setError('Google sign-in is unavailable right now. Please sign in with email and password.');
      console.error('[ContinueWithGoogleButton] signInWithRedirect failed', e);
    }
  };

  return (
    <div className="w-full space-y-2">
      <Button
        type="button"
        variant="outline"
        className="w-full h-11 rounded-[10px] flex items-center justify-center gap-2 border-[#E7D8C8] bg-white hover:bg-gray-50"
        onClick={handleGoogleClick}
        disabled={redirecting || blocked}
        aria-disabled={redirecting || blocked}
        aria-label="Continue with Google"
      >
        {redirecting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Redirecting to Google...
          </>
        ) : (
          <>
            {/* Google "G" mark, inline SVG (no external asset dependency) */}
            <svg className="h-4 w-4" viewBox="0 0 18 18" aria-hidden="true">
              <path
                fill="#4285F4"
                d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.616z"
              />
              <path
                fill="#34A853"
                d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.583-5.036-3.71H.957v2.332A8.997 8.997 0 0 0 9 18z"
              />
              <path
                fill="#FBBC05"
                d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A9 9 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z"
              />
              <path
                fill="#EA4335"
                d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z"
              />
            </svg>
            Continue with Google
          </>
        )}
      </Button>
      {error && <p className="text-sm text-red-500 text-center">{error}</p>}
      {/* consent-on-google-signup: explain WHY the button is disabled (the
          RegisterPage Sign-Up button is likewise gated on the same checkbox).
          Shown only on the signup door while the box is unticked. Consent is
          the earlier gate in the form, so it takes priority over the code
          notice when both are blocking (US-005 T2 error_scenarios_to_handle:
          never render two conflicting notices). */}
      {gatesDisableButton && consentBlocked && !error && (
        <p className="text-xs text-gray-500 text-center" role="status" aria-live="polite">
          Please accept the Terms of Service and Privacy Policy above to continue with Google.
        </p>
      )}
      {/* promo-code US-005 (T2) / AC-005.1 / AC-005.5: dash-free explanatory
          notice when the code gate (not consent) is the reason this button is
          disabled. Reuses the SAME role=status aria-live=polite pattern as the
          consent notice above so assistive tech announces it consistently
          (WCAG 2.1 AA, non-colour-only disabled-state cue). */}
      {gatesDisableButton && codeGateBlocked && !consentBlocked && !error && (
        <p className="text-xs text-gray-500 text-center" role="status" aria-live="polite">
          {codeBlockedNotice || DEFAULT_CODE_BLOCKED_NOTICE}
        </p>
      )}
    </div>
  );
}
